import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { 
  LayoutDashboard, 
  FolderKanban, 
  Users, 
  Plug2, 
  LogOut,
  ChevronRight,
  ChevronDown,
  Building2,
  FileBarChart,
  ClipboardList,
  PlusCircle,
  FolderOpen,
  Truck,
  Database,
  MessageSquare,
  FlaskConical,
  BarChart3
} from "lucide-react";

// NavItem component defined outside PremiumSidebar
function NavItem({ item, index, isSubItem = false, isActive }) {
  const Icon = item.icon;
  const active = isActive(item.path);
  
  return (
    <NavLink
      to={item.path}
      data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
    >
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        className={`relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group ${
          isSubItem ? 'ml-4' : ''
        } ${
          active 
            ? 'bg-gradient-to-r from-vcc-pink-100 to-vcc-cream-100 text-vcc-teal shadow-sm' 
            : 'text-slate-600 hover:text-vcc-teal hover:bg-vcc-pink-50/50'
        }`}
        whileHover={{ x: 5 }}
      >
        {/* Active indicator */}
        <AnimatePresence>
          {active && (
            <motion.div
              initial={{ opacity: 0, scaleY: 0 }}
              animate={{ opacity: 1, scaleY: 1 }}
              exit={{ opacity: 0, scaleY: 0 }}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-vcc-teal rounded-r-full"
            />
          )}
        </AnimatePresence>
        
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
          active 
            ? 'bg-vcc-teal/10' 
            : 'bg-white/60 group-hover:bg-vcc-pink-100'
        }`}>
          <Icon className={`w-5 h-5 ${active ? 'text-vcc-teal' : ''}`} />
        </div>
        
        <span className="font-poppins font-medium flex-1">{item.label}</span>
        
        {active && (
          <ChevronRight className="w-4 h-4 text-vcc-teal" />
        )}
      </motion.div>
    </NavLink>
  );
}

export default function PremiumSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [requirementsOpen, setRequirementsOpen] = useState(true);
  const [productDevOpen, setProductDevOpen] = useState(true);
  
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Main nav items (before Requirements folder)
  const mainNavItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  ];

  // Requirements folder items
  const requirementItems = [
    { path: "/client-requirement", icon: PlusCircle, label: "New Requirement Form" },
    { path: "/client-requirements", icon: ClipboardList, label: "Client Requirements" },
  ];

  // Product Development folder items
  const productDevItems = [
    { path: "/products", icon: FolderOpen, label: "Product Development" },
    { path: "/product-development-report", icon: FileBarChart, label: "Development Report" },
    { path: "/client-requirement-dashboard", icon: BarChart3, label: "Requirement Dashboard" },
  ];

  // Other nav items (after Product Development folder)
  const otherNavItems = [
    { path: "/sample-dispatches", icon: Truck, label: "Sample Dispatches" },
    { path: "/sample-feedback-report", icon: MessageSquare, label: "Sample Feedback Report" },
    { path: "/sample-development-report", icon: FlaskConical, label: "Sample Development" },
    { path: "/clients", icon: Building2, label: "Client List" },
    { path: "/reports", icon: FileBarChart, label: "Reports" },
    { path: "/sync-queue", icon: Database, label: "Sync Queue" },
  ];

  // Admin only items
  const adminItems = user?.role === "Admin" || user?.role === "admin" ? [
    { path: "/users", icon: Users, label: "User Management" },
    { path: "/integration", icon: Plug2, label: "Integration Center" }
  ] : [];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isRequirementsSectionActive = requirementItems.some(item => isActive(item.path));
  const isProductDevSectionActive = productDevItems.some(item => isActive(item.path));

  return (
    <motion.div 
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      className="w-72 h-screen bg-gradient-to-b from-white via-vcc-cream-50 to-vcc-pink-50 border-r border-vcc-pink-100 flex flex-col fixed left-0 top-0 z-40 shadow-lg"
    >
      {/* Logo Section */}
      <div className="p-5 border-b border-vcc-pink-100/50">
        <motion.div 
          className="flex items-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <img 
            src="/vcc-logo.png" 
            alt="V Cosmetic Crafters" 
            className="w-12 h-12 object-contain"
          />
          <div>
            <h1 className="font-poppins font-bold text-lg text-slate-800 leading-tight">V Cosmetic Crafters</h1>
            <p className="text-xs text-slate-500 font-poppins">Private Limited</p>
          </div>
        </motion.div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <p className="text-xs text-slate-400 uppercase tracking-wider font-poppins px-3 mb-4 font-medium">Main Menu</p>
        
        {/* Main nav items */}
        {mainNavItems.map((item, index) => (
          <NavItem key={item.path} item={item} index={index} isActive={isActive} />
        ))}

        {/* Requirements Folder */}
        <div className="mt-2">
          <motion.button
            onClick={() => setRequirementsOpen(!requirementsOpen)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
              isRequirementsSectionActive
                ? 'bg-gradient-to-r from-vcc-coral/10 to-vcc-pink-100 text-vcc-coral'
                : 'text-slate-600 hover:text-vcc-coral hover:bg-vcc-pink-50/50'
            }`}
            whileHover={{ x: 5 }}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
              isRequirementsSectionActive
                ? 'bg-vcc-coral/10'
                : 'bg-white/60'
            }`}>
              <FolderOpen className={`w-5 h-5 ${isRequirementsSectionActive ? 'text-vcc-coral' : ''}`} />
            </div>
            <span className="font-poppins font-medium flex-1 text-left">Requirements</span>
            <motion.div
              animate={{ rotate: requirementsOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className={`w-4 h-4 ${isRequirementsSectionActive ? 'text-vcc-coral' : 'text-slate-400'}`} />
            </motion.div>
          </motion.button>
          
          {/* Collapsible sub-items */}
          <AnimatePresence>
            {requirementsOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-1 space-y-1 border-l-2 border-vcc-pink-200 ml-7">
                  {requirementItems.map((item, index) => (
                    <NavItem key={item.path} item={item} index={index} isSubItem isActive={isActive} />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Product Development Folder */}
        <div className="mt-2">
          <motion.button
            onClick={() => setProductDevOpen(!productDevOpen)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
              isProductDevSectionActive
                ? 'bg-gradient-to-r from-vcc-teal/10 to-vcc-cream-100 text-vcc-teal'
                : 'text-slate-600 hover:text-vcc-teal hover:bg-vcc-cream-50/50'
            }`}
            whileHover={{ x: 5 }}
            data-testid="nav-product-development-section"
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
              isProductDevSectionActive
                ? 'bg-vcc-teal/10'
                : 'bg-white/60'
            }`}>
              <FolderKanban className={`w-5 h-5 ${isProductDevSectionActive ? 'text-vcc-teal' : ''}`} />
            </div>
            <span className="font-poppins font-medium flex-1 text-left">Product Development</span>
            <motion.div
              animate={{ rotate: productDevOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className={`w-4 h-4 ${isProductDevSectionActive ? 'text-vcc-teal' : 'text-slate-400'}`} />
            </motion.div>
          </motion.button>
          
          {/* Collapsible sub-items */}
          <AnimatePresence>
            {productDevOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-1 space-y-1 border-l-2 border-vcc-teal/30 ml-7">
                  {productDevItems.map((item, index) => (
                    <NavItem key={item.path} item={item} index={index} isSubItem isActive={isActive} />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Other nav items */}
        {otherNavItems.map((item, index) => (
          <NavItem key={item.path} item={item} index={index + 3} isActive={isActive} />
        ))}

        {/* Admin items */}
        {adminItems.map((item, index) => (
          <NavItem key={item.path} item={item} index={index + 6} isActive={isActive} />
        ))}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-vcc-pink-100/50">
        <motion.div 
          className="flex items-center gap-3 p-3 rounded-xl bg-white/60 backdrop-blur-sm mb-3 shadow-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-vcc-teal to-vcc-coral flex items-center justify-center text-white font-poppins font-bold">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate font-poppins">{user?.name || 'User'}</p>
            <p className="text-xs text-slate-500 truncate font-poppins">{user?.role || 'Member'}</p>
          </div>
        </motion.div>
        
        <motion.button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all duration-300 group"
          whileHover={{ x: 5 }}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-poppins font-medium">Sign Out</span>
        </motion.button>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-32 left-4 w-32 h-32 bg-vcc-pink-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-0 w-24 h-24 bg-vcc-cream-200/30 rounded-full blur-2xl pointer-events-none" />
    </motion.div>
  );
}
